import { CopyIcon, CustomButton, Modal } from '@ui-kit'
import { useCallback, useMemo } from 'react'
import {
  JsonStructureDetailsProps,
  JsonStructureDetailsModalProps,
} from '../../types/types'
import { JSON_STRUCTURE_MODAL } from '../../utils/constants'
import { copyToClipboard, formatJson } from '../../utils/integrationLogs.utils'

export const JsonStructureDetails = ({
  request,
  response,
  onClose,
  requestTitle = JSON_STRUCTURE_MODAL.REQUEST_TITLE,
  responseTitle = JSON_STRUCTURE_MODAL.RESPONSE_TITLE,
  cancelLabel = JSON_STRUCTURE_MODAL.CANCEL_LABEL,
}: JsonStructureDetailsProps) => {
  const requestJson = useMemo(() => formatJson(request), [request])
  const responseJson = useMemo(() => formatJson(response), [response])

  const handleCopy = useCallback(async (jsonText: string) => {
    try {
      await copyToClipboard(jsonText)
    } catch {
      await copyToClipboard(jsonText)
    }
  }, [])

  return (
    <div className="max-w-full max-h-full min-h-0 flex flex-col overflow-hidden rounded-[0.25rem]">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-2 flex-shrink-0">
        <h2 className="text-base font-semibold">
          {JSON_STRUCTURE_MODAL.TITLE}
        </h2>
        <CustomButton onClick={onClose}>{cancelLabel}</CustomButton>
      </div>

      <div className="h-[0.0625rem] bg-gray-200 flex-shrink-0" />

      {/* Body */}
      <div className="p-3 flex-1 overflow-hidden min-h-0">
        <div className="flex gap-[0.625rem] h-full min-h-0">
          {/* Request Panel */}
          <div className="flex-1 flex min-w-0 flex-col bg-gray-50 border rounded-md p-2 overflow-hidden min-h-0">
            <div className="h-[1.5rem] flex justify-between items-center flex-shrink-0">
              <span className="text-sm font-medium">{requestTitle}</span>
              <button onClick={() => handleCopy(requestJson)} type="button">
                <CopyIcon width="0.75rem" height="0.875rem" />
              </button>
            </div>

            <div className="mt-1 flex-1 bg-white border rounded-md p-2 overflow-hidden min-h-0">
              <pre className="h-full overflow-y-auto custom-scrollbar text-xs whitespace-pre-wrap break-words m-0">
                {requestJson}
              </pre>
            </div>
          </div>

          {/* Response Panel */}
          <div className="flex-1 min-w-0 flex flex-col bg-gray-50 border rounded-md p-2 overflow-hidden min-h-0">
            <div className="h-[1.5rem] flex justify-between items-center flex-shrink-0">
              <span className="text-sm font-medium">{responseTitle}</span>
              <button onClick={() => handleCopy(responseJson)} type="button">
                <CopyIcon width="0.75rem" height="0.875rem" />
              </button>
            </div>

            <div className="mt-1 flex-1 bg-white border rounded-md p-2 overflow-hidden min-h-0">
              <pre className="h-full overflow-y-auto custom-scrollbar text-xs whitespace-pre-wrap break-words m-0">
                {responseJson}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const JsonStructureDetailsModal = ({
  isOpen,
  closeModal,
  request,
  response,
  requestTitle,
  responseTitle,
  cancelLabel,
}: JsonStructureDetailsModalProps) => {
  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      isDragable
      isBackdropInteractive
      isResizable
      width="w-[47.625rem] h-[17.625rem]"
      bodyCssClassName="[&_.modal-scroll-container]:!overflow-hidden [&_.modal-body-container]:!h-full [&_.modal-body-container]:!min-h-0"
    >
      <div className="h-full min-h-0 max-h-[90vh] overflow-hidden flex flex-col">
        <JsonStructureDetails
          request={request}
          response={response}
          onClose={closeModal}
          requestTitle={requestTitle}
          responseTitle={responseTitle}
          cancelLabel={cancelLabel}
        />
      </div>
    </Modal>
  )
}
